import { useState, useEffect } from 'react';
import { LoadingIndicator } from '../../lib/iprs-react-library/src/index.js';
import useAppointmentTypeToggle from '../Team/useAppointmentTypeToggle';
import userLoggedInHasRole from '../Util/userLoggedInHasRole';
import Notice from '../Util/Notice';

const NO_GROUPS = [];

/*
 * Settings section for including/excluding appointment types per team group.
 * The team group list in state is refreshed by RotaTypeGroupSettings when
 * Settings opens, so it isn't reloaded here.
 */
const AppointmentTypeGroupSettings = ({ state }) => {

    const { api } = state;

    const groups = state.team?.Groups ?? NO_GROUPS;

    const hasDiaryManagerPerm = userLoggedInHasRole(api, 'Diary Manager');

    const [selectedGroupID, setSelectedGroupID] = useState(null);
    const [notice, setNotice] = useState(null); // { text, isError }, cleared by the next action

    const { appointmentTypes, savingAppointmentTypeID, isIncluded, toggleAppointmentType } =
        useAppointmentTypeToggle(api, setNotice);

    // Default to the first team group once the list has loaded
    useEffect(() => {
        const selectedStillExists = groups.some(g => g.GroupID === selectedGroupID);
        if (!selectedStillExists) {
            setSelectedGroupID(groups.length ? groups[0].GroupID : null);
        }
    }, [groups, selectedGroupID]);

    const selectedGroup = groups.find(g => g.GroupID === selectedGroupID);

    const onSelectGroup = e => {
        setNotice(null);
        setSelectedGroupID(Number.parseInt(e.target.value, 10));
    };

    return (
        <div>
            <Notice notice={notice} onDismiss={() => setNotice(null)} />

            <section className='settings-section'>
                <h3>Team Appointment Types</h3>
                <p>Every appointment type is available to every team group by default.
                    Pick a team group, then include or exclude its appointment types.
                    Clinicians can't be made available for an appointment type that is
                    excluded for all of their teams' groups.</p>

                {!groups.length && <p>No team groups found.</p>}

                {!!groups.length &&
                    <ul className='settings-list'>
                        <li>
                            <span className='settings-list__label'><strong>Team group</strong></span>
                            <select value={selectedGroupID ?? ''} onChange={onSelectGroup} >
                                {groups.map(g => <option key={g.GroupID} value={g.GroupID}>{g.GroupTitle}</option>)}
                            </select>
                        </li>
                    </ul>
                }

                {selectedGroup &&
                    <>
                        <h4>{selectedGroup.GroupTitle} appointment types</h4>
                        <div className='settings-list-scroll'>
                            <ul className='settings-list'>
                                {appointmentTypes.map(at => {
                                    const included = isIncluded(at, selectedGroupID);
                                    const saving = savingAppointmentTypeID === at.AppointmentTypeID;
                                    return (
                                        <li key={at.AppointmentTypeID}>
                                            <span className='settings-list__label'>{at.Title}</span>
                                            {saving && <LoadingIndicator loading={true} size={16} />}
                                            <button type='button'
                                                className={'settings-table__toggle ' +
                                                    (included ? 'settings-table__toggle--included' : '')}
                                                disabled={!hasDiaryManagerPerm || saving}
                                                title={hasDiaryManagerPerm
                                                    ? `Click to ${included ? 'exclude from' : 'include for'} ${selectedGroup.GroupTitle}`
                                                    : undefined}
                                                onClick={() => toggleAppointmentType(at, selectedGroupID)} >
                                                {included ? 'Included' : 'Excluded'}
                                            </button>
                                        </li>
                                    );
                                })}
                                {0 === appointmentTypes.length &&
                                    <li><span className='settings-list__label'>No appointment types found.</span></li>
                                }
                            </ul>
                        </div>
                    </>
                }
            </section>
        </div>
    );
};

export default AppointmentTypeGroupSettings;
